import type { RouteLocationNormalized } from 'vue-router'
import { CONFIG } from '@/scripts/app-configs/constants'
import { PATIENT_UNIT } from '@/scripts/configs/patient-units'
import type { UserMeType, UserFlagsType } from '@/types/app-data/user.types'
import { segmentSDK } from '@/utils/segment'
import {
  EVENT_SCHEMAS,
  EVENT_REGISTRY,
  EventKeysType,
  EventPayloads,
} from '@/utils/segment/segment-events'
import { SegmentUser } from '@/utils/segment/segment.types'

const debug = CONFIG.segmentCustomDebug

type TrackEventOptions = {
  ignoreValidation?: boolean
  user?: SegmentUser
}

/**
 * Main Segment event tracker.
 * Validates event key and payload before sending to Segment.
 */
const trackEvent = <T extends EventKeysType>(
  key: T,
  payload: EventPayloads[T],
  { ignoreValidation, user }: TrackEventOptions = {},
) => {
  const eventKey = EVENT_REGISTRY[key]?.key
  const name = EVENT_REGISTRY[key]?.name
  const schema = EVENT_SCHEMAS[key]
  const additionalProps = user ? { user: { ...user } } : {}

  if (!eventKey || !schema) {
    if (debug) {
      if (!eventKey) console.warn('[Segment] Invalid event key:', key)
      if (!schema) console.warn('[Segment] Missing schema for event:', key)
    }
    if (!ignoreValidation) return
  }

  if (schema) {
    const validation = schema.safeParse(payload)

    if (!validation.success) {
      if (debug)
        console.warn('[Segment] Invalid payload for', key, validation.error.flatten())
      if (!ignoreValidation) return
    }
  }

  segmentSDK.track(eventKey || name || String(key), {
    ...payload,
    ...additionalProps,
  } as Record<string, unknown>)
}

/* Tracks an event and automatically send the current Segment user. */
export const trackEventWithUser = async <T extends EventKeysType>(
  key: T,
  payload: EventPayloads[T],
  options: TrackEventOptions = {},
) => {
  // Always use the async method for consistent user retrieval
  // This ensures we have the most up-to-date user information
  const user = await segmentSDK.getSegmentUser()

  trackEvent(key, payload, {
    ...options,
    ...(user ? { user: user as SegmentUser } : {}),
  })
}

/* Tracks a page view event for Segment. */
export const trackPageView = (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  segmentSDK.page(to.name as string ?? 'page', {
    path: to.fullPath,
    referrerPath: from.fullPath,
    referrerName: from.name || '',
  })
}

/**
 * Identifies a user for Segment tracking.
 * Should be called after login or when user data changes.
 */
export const identifyUser = ({
  user,
  flags,
}: {
  user: UserMeType
  flags: UserFlagsType
}) => {
  const unit = user.unit === PATIENT_UNIT.SWORD ? 'thrive' : user.unit

  segmentSDK.identify(`${user.id}`, {
    fullName: `${user.firstname} ${user.lastname}`,
    unit,
    isIntakePt: flags.isIntakePt,
    isLeadPt: flags.isLeadPt,
    isTreatmentPt: flags.isTreatmentPt,
    isFLabsPt: flags.isFLabsPt,
  })
}
